import React, { useEffect, useMemo } from 'react';
import { Field, FieldTypes } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import { toNumber } from 'lodash';

import { DEFAULT_DATE_FORMAT, EMPTY_FIELD_VALUE } from '@/common/constants';
import { Address } from '@/common/types';
import { CheckDetails, GENERIC_FORM_TYPES, LOCKBOX_TYPES_OPTIONS, LockboxStatus, LockboxType } from '@/modules/employer/components/Lockbox/lockbox.types';
import { 
  LockboxAmountInput,
  LockboxDocumentTypeInput,
  LockboxFormNameInput,
  LockboxVendorAddress,
  LockboxVendorDropdown
} from '@/modules/employer/components/Lockbox/LockboxDetails/components/LockboxInfo/LockboxForm/components';
import useLockboxDetailsStore from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDetails.store';
import { Vendor } from '@/modules/employer/components/Lockbox/queries/useGetVendors.query';
import { formatCurrency, getFullAddress } from '@/utils';

interface Props {
  vendors?: Vendor[];
}

const useLockboxRecordFields = ({ vendors }: Props) => {
  const { editableLockbox, originalLockbox, setEditableLockbox } = useLockboxDetailsStore();
  const vendorId = editableLockbox?.vendorId;

  const activeVendor = useMemo(() => vendors
    ?.find((vendor) => vendor?.vendorId === vendorId),
  [vendorId, vendors]);

  const lockboxTypeTitle = useMemo(() => LOCKBOX_TYPES_OPTIONS
    .find((option) => option.key === originalLockbox?.lockboxType)?.title, [originalLockbox?.lockboxType]);

  const checkDetails = originalLockbox?.checkDetails;

  const checkDetailsAddress: Address = useMemo(() => ({
    line1: checkDetails?.addressLine1,
    line2: checkDetails?.addressLine2,
    city: checkDetails?.city,
    state: {
      name: checkDetails?.state,
    },
    zipcode: checkDetails?.zip,
  }), [checkDetails]);

  const vendorAddress: Address = useMemo(() => ({
    line1: activeVendor?.addressLine1,
    line2: activeVendor?.addressLine2,
    city: activeVendor?.city,
    state: {
      name: activeVendor?.state,
    },
    zipcode: activeVendor?.zip,
  }), [activeVendor]);

  const checkDetailsFullAddress = useMemo(() => getFullAddress(checkDetailsAddress, true),
    [checkDetailsAddress]);

  const vendorFullAddress = useMemo(() => getFullAddress(vendorAddress, true),
    [vendorAddress]);

  const isVendorFromCheckDetails = useMemo(() => activeVendor?.vendorId && activeVendor?.vendorId === checkDetails?.vendorId,
    [checkDetails, activeVendor]);

  const currentAddress = useMemo(() => isVendorFromCheckDetails ? checkDetailsFullAddress : vendorFullAddress,
    [isVendorFromCheckDetails, checkDetailsFullAddress, vendorFullAddress]);

  const currentCheckDetails: CheckDetails = useMemo(() => ({
    ...(isVendorFromCheckDetails ? checkDetails : activeVendor),
    benefitOf: originalLockbox?.checkDetails?.benefitOf || originalLockbox?.ocr?.employeeName,
  }), [originalLockbox, checkDetails, isVendorFromCheckDetails, activeVendor]);

  useEffect(() => {
    setEditableLockbox({ checkDetails: currentCheckDetails });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCheckDetails?.vendorId]);

  const lockboxRecordFields = useMemo(() => [
    {
      type: FieldTypes.Node,
      name: 'documentType',
      label: 'Document type',
      value: lockboxTypeTitle || EMPTY_FIELD_VALUE,
      node: <LockboxDocumentTypeInput />,
    },
    ...(editableLockbox?.lockboxType === LockboxType.HSA_FORM ? [{
      type: FieldTypes.Node,
      name: 'formName',
      label: 'Form name',
      value: editableLockbox?.formName || EMPTY_FIELD_VALUE,
      node: <LockboxFormNameInput />,
    }] : []),
    {
      type: FieldTypes.Text,
      name: 'dateReceived',
      label: 'Date received',
      value: originalLockbox?.requestDate
        ? dayjs(originalLockbox?.requestDate).format(DEFAULT_DATE_FORMAT)
        : EMPTY_FIELD_VALUE,
    },
    ...(!originalLockbox?.ocr && editableLockbox?.lockboxType !== LockboxType.RETURNED_MAIL ? [
      {
        type: FieldTypes.Node,
        name: 'amount',
        label: 'Amount',
        value: originalLockbox?.amount
          ? formatCurrency(toNumber(originalLockbox?.amount))
          : EMPTY_FIELD_VALUE,
        node: <LockboxAmountInput />,
      }] : []),
    ...(editableLockbox?.lockboxType 
      && editableLockbox?.lockboxType !== LockboxType.CLAIM_REPAYMENT
      && editableLockbox?.lockboxType !== LockboxType.UNKNOWN
      && editableLockbox?.lockboxType !== LockboxType.RETURNED_MAIL
      && !GENERIC_FORM_TYPES.includes(editableLockbox.lockboxType)
      && !editableLockbox?.isMultipleRecordsWarning
      && originalLockbox?.lockboxStatus !== LockboxStatus.DIVIDED ? [
        {
          type: FieldTypes.Node,
          name: 'vendor',
          label: 'Vendor',
          value: activeVendor?.vendorName || EMPTY_FIELD_VALUE,
          node: <LockboxVendorDropdown vendors={vendors} />,
        },
        ...(activeVendor ? [
          {
            type: FieldTypes.Node,
            name: 'vendorAddress',
            label: 'Vendor address',
            value: currentAddress || EMPTY_FIELD_VALUE,
            node: <LockboxVendorAddress checkDetailsAddress={currentAddress} checkDetails={currentCheckDetails} />,
          }
        ] : []),
      ] : []),
  ], [vendors, activeVendor, editableLockbox, originalLockbox, lockboxTypeTitle, currentAddress, currentCheckDetails]);

  return {
    lockboxRecordFields: lockboxRecordFields as Field[],
  };
};

export default useLockboxRecordFields;
